import React, { FC, ReactNode, useContext, useEffect, useState, PropsWithChildren } from 'react';

import { sendBeaconEvent, useCookie, useFeatureFlags, isBrowser } from '@lucid/utils';
import throttle from 'lodash/throttle';

import Box from '../box/box';
import ABTestContext from './ab-test-context';
import LayoutContext from '../context/layout-context';
import { getVariationAssignment } from './ab-test-helpers';
import ABTestBranch from './ab-test-branch';
import ABTestShield, { ShieldProps } from './ab-test-shield';
import { ABTesting } from '../../types/types';

export interface ABTestProps {
  /**  Test is visible without scrolling */
  isAboveTheFold?: boolean;
  /**  Explicitly define the branches you'll be using to avoid discrepancies with feature flag variation allocations */
  branchesToRender: ABTesting.Variation[];
  /** Allows the test wrapper to get additional styles. */
  className?: string;
  /** The children of the AB Test Component */
  children?: ReactNode;
  /** The default branch when test is inactive */
  defaultBranch?: ABTesting.Variation;
  /** The delay for the AB Test trigger (must use 'delay' trigger)*/
  delay?: number;
  /** Manual pageYOffset to trigger the test (must use 'scroll' trigger) */
  distance?: number;
  /** Sets conditions under which to run the AB Test */
  hasConditionalTrigger?: boolean;
  /** Selects element that will trigger test */
  selector?: string;
  /** The branch that will be rendered in SSR */
  ssrBranch?: ABTesting.Variation;
  /** String to match with corresponding Feature Flag to activate test and allocate traffic */
  test: string;
  /** The method by which the test will be triggered */
  trigger?: ABTesting.Triggers;
  /** Will automatically trigger AB Test for unit testing */
  isTestMode?: boolean;
  pollingFunc?: () => Promise<any>;
}

export interface SubComponents {
  /** The AB Test branch */
  Branch: FC<PropsWithChildren<ABTesting.BranchProps>>;
  /** The Context for the AB Test */
  Context: typeof ABTestContext;
  /** Renders white shield over page until test has loaded */
  Shield: FC<PropsWithChildren<ShieldProps>>;
}

const ABTest: FC<PropsWithChildren<ABTestProps>> & SubComponents = props => {
  const marketingTestConfig = useFeatureFlags();
  const {
    isAboveTheFold = false,
    branchesToRender = [],
    children = null,
    className,
    defaultBranch = ABTesting.Variation.A,
    delay,
    distance = 0,
    hasConditionalTrigger = true,
    selector = '',
    ssrBranch = ABTesting.Variation.A,
    test,
    trigger = 'load',
    isTestMode = false,
    pollingFunc,
  } = props;

  const featureFlag = marketingTestConfig.get(test);
  const testBranches = featureFlag ? featureFlag.variations : null;
  const isInitialActive = featureFlag ? Boolean(featureFlag.isActive && hasConditionalTrigger) : true;
  const [isAssigned, setIsAssigned] = useState(false);
  const [branch, setBranch] = useState<ABTesting.Variation>(ssrBranch);
  const [intervals] = useState<NodeJS.Timer[]>([]);
  const [isTestActive, setTestIsActive] = useState(isInitialActive);
  const [hasTriggerFired, setHasTriggerFired] = useState(false);
  const { abTesting } = useContext(LayoutContext);

  const storageKey = `abTest_${test}`;
  let delayTimeout: NodeJS.Timeout | undefined;

  const userId = useCookie('userId');

  const hasUIActive = () => {
    if (isBrowser()) {
      return window.localStorage.getItem('showAbTestingUi') === '1';
    }

    return false;
  };

  const triggerCurrentTest = () => {
    if (hasTriggerFired || !branch) return;
    if (isTestMode) return setHasTriggerFired(true);
    const normalizedBranch = branch.replace(/_/g, '-');
    const property = {
      [test]: normalizedBranch,
    };

    const event = {
      name: 'assignedToTest',
      userId: Number(userId) || -1,
      testName: test,
      testArm: normalizedBranch,
      teamName: 'Growth Front-end',
    };

    if (!isTestActive) {
      if (hasUIActive()) {
        console.info('Test is not active, but if it was it would have fired the following property:', property);
        console.info(
          '%c Test is not active, but if it was it would have fired the following property for Beacon:',
          'color: #7DC3E3; font-weight: bold;',
          event,
        );
      }
      return setHasTriggerFired(true);
    }

    if (hasUIActive()) {
      console.info('Would have fired the following property:', property);
      console.info('%c Would have fired the following event for Beacon:', 'color: #06A9F4; font-weight: bold', event);

      return setHasTriggerFired(true);
    }

    if (branch) {
      console.log('1', { branch });
      window.localStorage.setItem(storageKey, branch);
    }

    sendBeaconEvent(event)
      .then(response => {
        console.info('Beacon reported:', response);
        return setHasTriggerFired(true);
      })
      .finally(() => {
        console.info('Beacon attempted');
        return setHasTriggerFired(true);
      });
  };

  const updateBranch = (updatedBranch: ABTesting.Variation) => {
    setBranch(updatedBranch);
    setHasTriggerFired(false);
  };

  const updateTestInterface = () => {
    if (hasUIActive()) {
      const currentTest = marketingTestConfig.get(test);
      const branches = currentTest ? currentTest.variations : null;
      abTesting?.updateTest({
        ...props,
        branch,
        trigger,
        hasTriggerFired,
        isTestActive,
        isAboveTheFold,
        branches,
        updateBranch,
      });
    }
  };

  const handleScrollTrigger = () => {
    const handleScroll = throttle(() => {
      const currentY = window.pageYOffset;
      if (currentY >= distance) {
        triggerCurrentTest();
        window.removeEventListener(trigger, handleScroll);
      }
    }, 500);

    window.addEventListener('scroll', handleScroll);
  };

  const handleVisibleTrigger = () => {
    // placeholder
  };

  const handleTrigger = async () => {
    if (pollingFunc) {
      await pollingFunc();
    }

    if (selector) {
      const elements = Array.from(document.querySelectorAll(selector));
      elements.map(element => {
        const triggerHandler = () => {
          triggerCurrentTest();
          element.removeEventListener(trigger, triggerHandler);
        };
        element.addEventListener(trigger, triggerHandler);
      });
    }
  };

  const handleRedirectTrigger = () => {
    triggerCurrentTest();
  };

  const handleDelayTrigger = () => {
    if (!delay) return;

    const delayMilliseconds = delay * 1000;
    delayTimeout = setTimeout(() => {
      triggerCurrentTest();
    }, delayMilliseconds);
  };

  const refreshTriggers = () => {
    switch (trigger) {
      case 'load':
        triggerCurrentTest();
        break;
      case 'delay':
        handleDelayTrigger();
        break;
      case 'redirect':
        handleRedirectTrigger();
        break;
      case 'scroll':
        handleScrollTrigger();
        break;
      case 'visible':
        handleVisibleTrigger();
        break;
      default:
        handleTrigger();
        break;
    }
  };

  const setup = () => {
    try {
      if (!marketingTestConfig.has(test)) {
        throw Error(`AB Test not present in feature flag config: ${test}.`);
      }

      if (hasConditionalTrigger) {
        const { branch: assignedBranch, isTestActive: isActive } = getVariationAssignment({
          branchesToRender,
          test,
          testBranches,
          storageKey,
          isDevMode: hasUIActive(),
        });
        setBranch(assignedBranch);
        setTestIsActive(isActive);
        setIsAssigned(true);
      } else {
        setHasTriggerFired(true);
        setBranch(defaultBranch);
        setTestIsActive(false);
      }
    } catch (error) {
      setTestIsActive(false);
      console.error(error);
    }
  };

  useEffect(() => {
    setup();
    return () => {
      intervals.map(clearInterval);
      if (delayTimeout) {
        clearTimeout(delayTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (branch && isAssigned) {
      updateTestInterface();
      refreshTriggers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, hasTriggerFired, isAssigned]);

  const shouldShowShield = isTestActive && isAboveTheFold && trigger === 'load' && !hasTriggerFired;

  const context = {
    test,
    branch,
    trigger,
    selector,
    hasTriggerFired,
    triggerCurrentTest,
    shouldShowShield,
    branchesToRender,
  };

  let shouldRender = trigger === 'redirect' && isTestActive ? hasTriggerFired : true;

  if (!isBrowser()) {
    shouldRender = true;
  }

  return (
    <ABTest.Context.Provider value={context}>
      <Box className={className} id={`ab-test-${test}`}>
        <ABTest.Shield shouldShowShield={shouldShowShield} />
        {shouldRender && children}
      </Box>
    </ABTest.Context.Provider>
  );
};

ABTest.Branch = ABTestBranch;
ABTest.Context = ABTestContext;
ABTest.Shield = ABTestShield;

export { ABTest };
export default ABTest;
